/**
 * MARKERS (owner B5): dots, tree-ring clusters, labels, glass pins, the site mark, ground pulses.
 * Implements MarkersAPI (contracts/map.ts); visual spec §D.1–D.4, §E.1, §E.4, §E.9, §H.
 *
 *   const mk = createMarkers(map, { beforeId: ANCHORS.places, topId: ANCHORS.top, reducedMotion });
 *
 * GL layers (just beneath ANCHORS.places, bottom → top):
 *   ld-places-cluster-ring2/-ring1, ld-places-cluster (+ hover rings), ld-places-cluster-count   z < 10
 *   ld-places-dot (group A), -b, -c, -d (one layer per group: constant colours ease with the mood)
 *   ld-places-dot-hover   the --ld-pulse ring (light arriving)
 *   ld-places-label       §D.1a label policy, as a filter recomputed on tier/group/category/selection
 * and at ANCHORS.top: ld-pin-proxy, invisible symbol twins of the DOM pins, the site mark and its whole
 * label box, and the legal attribution line (screen-fixed, re-anchored when the camera rests), so every
 * label below routes around them; and ld-loc-route-proxy, an invisible line-placed twin of the selected
 * route, so point labels step off the lit line (L1-13).
 *
 * Per-feature values (group activity, dimming, hover, "is a pin") are feature-state tweened on the ticker.
 * DOM pins are a pool of ≤ 25 maplibregl.Markers using the design's .ld-pin markup; icons are the design
 * system's <Icon/> rendered once (dom.tsx). Nothing here renders React per frame.
 *
 * Events (bus): "map:click-place" {id} (dot, pin, or a corridor → its road/future place), "map:hover-place"
 * {id | null} after a 120 ms dwell (never in kiosk), "map:click-empty". A cluster click zooms in.
 *
 * The store: data at creation (setPlaces replaces it), group (active dots, labels), category (the zoom-rule
 * pins and labels, applied a microtask later so an explicit setFocusSet in the same tick wins), layout
 * (sizes), insets (pill flips) and layers.labels (place labels).
 */
import * as maplibregl from "maplibre-gl";
import type { ExpressionSpecification, FilterSpecification, GeoJSONSource, LayerSpecification, Map as MLMap, MapGeoJSONFeature, MapMouseEvent, PointLike } from "maplibre-gl";
import type { Feature, FeatureCollection, Point } from "geojson";
import type { Category, GroupId, LngLat, Place } from "@/contracts/data";
import { LAYER_IDS, SOURCE_IDS, type CreateMarkers, type MarkersAPI } from "@/contracts/map";
import type { Insets, Layout, Mood } from "@/contracts/store";
import { DUR, EASE, EASE_CSS, exitMs } from "@/design";
import { Animator, FeatureFx } from "@/map/layers/fx";
import { siteLabelPoint } from "@/map/controller";
import { FONTSTACKS, MOODS, MOOD_TRANSITION_MS } from "@/map/style/moods";
import { routeFor, thin } from "@/map/route/paths";
import { ticker } from "@/lib/ticker";
import { bus } from "@/store/bus";
import { appStore, getApp } from "@/store/store";
import { loadIcons, mountWhorl, setIcon, type WhorlHandle } from "./dom";
import {
  MAX_PINS,
  computePins,
  hasDot,
  hitTolerance,
  labelIds,
  nearest,
  parseKey,
  layoutPills,
  pillParts,
  pinAriaLabel,
  pinSizes,
  pulseRadius,
  siteMarkSize,
  textScale,
  tierFor,
  toMarkerPlaces,
  tucksUnderSite,
  type Candidate,
  type MarkerPlace,
  type Rect,
  type Tier,
} from "./logic";
import "./markers.css";

/* ─── Ids the contract does not name ───────────────────────────────────── */

const DOT_IDS: Record<GroupId, string> = {
  A: LAYER_IDS.placesDot,
  B: `${LAYER_IDS.placesDot}-b`,
  C: `${LAYER_IDS.placesDot}-c`,
  D: `${LAYER_IDS.placesDot}-d`,
};
export const MARKER_IDS = {
  dots: DOT_IDS,
  dotHover: `${LAYER_IDS.placesDot}-hover`,
  clusterHover: `${LAYER_IDS.placesCluster}-hover`,
  clusterHover2: `${LAYER_IDS.placesCluster}-hover2`,
  proxyImage: "ld-loc-pin-proxy",
  /** The site's name and meta line as one box (sized from the DOM label). */
  siteLabelImage: "ld-loc-site-label-proxy",
  /** The legal attribution line (sized from .ld-loc-hud-attr). */
  attrImage: "ld-loc-attr-proxy",
  routeProxy: "ld-loc-route-proxy",
  routeProxyImage: "ld-loc-route-proxy-dot",
} as const;

const GROUPS: GroupId[] = ["A", "B", "C", "D"];
const SITE_LABEL = "Ládera City";
const SITE_META = "Lohegaon · Pune";
const SITE_ARIA = "Ládera City, the site";
const PROXY_PX = 8;
/** The route twin (L1-13): a 10 px transparent icon every 24 px along the selected route. */
const ROUTE_PROXY_PX = 10;
const ROUTE_PROXY_SPACING = 24;
/** The credit the map text keeps clear of (owned by the HUD; read-only here). */
const ATTR_SELECTOR = ".ld-loc-hud-attr";
/** §D.2 (S5): clusters below z10 only. */
const CLUSTER_MAX_ZOOM = 9;

/** MarkersAPI plus B5 extensions (B4 may use them through this type). */
export interface MarkersHandle extends MarkersAPI {
  /** duration: the controller's transition (the sunrise is 2400). */
  setMood(m: Mood, o?: { duration?: number }): void;
  /** The current zoom tier (0 clusters · 1 dots · 2 pins · 3 pins with pills). */
  tier(): Tier;
  /** The intro's site beat: the Whorl fills 0 → 1 (and its light with it). Resolves false if superseded. */
  fillSite(o?: { durationMs?: number; from?: number }): Promise<boolean>;
  debug(): MarkersDebug;
}
export interface MarkersDebug {
  tier: Tier;
  pins: string[];
  labelled: string[];
  selected: string | null;
  focus: string[] | null;
  hovered: string | null;
  dimmed: boolean;
  site: { visible: boolean; progress: number; label: boolean };
  poolSize: number;
}

type Expr = ExpressionSpecification;
const fs = (k: string, d = 0): Expr => ["coalesce", ["feature-state", k], d] as Expr;
const NO_TR = { duration: 0, delay: 0 };

/* ─── Paint (§D.1, §D.2) ───────────────────────────────────────────────── */

const ACT = fs("act", 1);
const DIM = fs("dim");
const PIN = fs("pin");
const HOV = fs("h");
const r = (base: number): Expr => ["+", ["*", base, ["+", 0.6, ["*", 0.4, ACT]]], ["*", 1.5, HOV]] as Expr;
/** Active group z9 3.5 → z11 5 → z13 5.5; other groups 0.6 ×; hover + 1.5. */
const DOT_RADIUS: Expr = ["interpolate", ["linear"], ["zoom"], 9, r(3.5), 11, r(5), 13, r(5.5)] as Expr;
/** Active 1 (0.5 dimmed), others 0.45 (0.25 dimmed); hidden while the place is a glass pin. */
const DOT_OPACITY: Expr = ["*", ["-", ["+", 0.45, ["*", 0.55, ACT]], ["*", DIM, ["+", 0.2, ["*", 0.3, ACT]]]], ["-", 1, PIN]] as Expr;
const DOT_STROKE_W: Expr = ["*", 1.5, ACT, ["-", 1, PIN]] as Expr;
const PC: Expr = ["get", "point_count"] as Expr;
const clusterR = (plus: number): Expr => ["step", PC, 13 + plus, 5, 16 + plus, 10, 19 + plus, 25, 22 + plus] as Expr;
const IS_CLUSTER: FilterSpecification = ["has", "point_count"];
const NOT_CLUSTER: FilterSpecification = ["!", ["has", "point_count"]];
const NO_LABELS: FilterSpecification = ["==", ["get", "id"], "\u0000"];

/* ─── Pins ──────────────────────────────────────────────────────────────── */

interface PinView {
  key: string;
  placeId: string;
  extra: number | null;
  el: HTMLDivElement;
  fx: HTMLDivElement;
  btn: HTMLButtonElement;
  dot: HTMLSpanElement;
  pill: HTMLSpanElement;
  pillName: Text;
  pillMeta: HTMLElement;
  marker: maplibregl.Marker;
  attached: boolean;
  state: "in" | "leaving" | "free";
  anim: Animation | null;
  leaveTimer: ReturnType<typeof setTimeout> | null;
  icon: string;
  pillW: number;
  pillH: number;
  flip: boolean;
  /** The pill wraps inside this width (px) when neither side of the pin has room for it (L4-11). */
  max: number | null;
  /** A bloom pin under the site's name shows as its group dot instead (L1-02). */
  tucked: boolean;
  /** The rules ask for a persistent pill (z ≥ 13, not the selection). */
  wantPill: boolean;
  /** A persistent pill is shown (it fitted). */
  labelled: boolean;
}

/* ═══ Factory ═══════════════════════════════════════════════════════════ */

export function createMarkers(map: MLMap, o: { beforeId: string; topId: string; reducedMotion: () => boolean }): MarkersHandle {
  const app0 = getApp();
  let layout: Layout = app0.layout;
  let group: GroupId = app0.group;
  let category: string | null = app0.category;
  let insets: Insets = { ...app0.insets };
  let labelsFlag = app0.layers.labels;
  let mood: Mood = app0.mood;
  let destroyed = false;
  const reduced = (): boolean => {
    try {
      return !!o.reducedMotion();
    } catch {
      return false;
    }
  };
  const kiosk = (): boolean => layout === "kiosk";

  let places: MarkerPlace[] = [];
  let byId = new Map<string, MarkerPlace>();
  /** corridor id (roads/future geojson) → the road or future place that owns it */
  let corridorPlace = new Map<string, string>();
  let selectedId: string | null = null;
  let focus: string[] | null = null;
  let extHover: string | null = null;
  let ptrHover: { kind: "dot" | "cluster"; id: string | number } | null = null;
  let dimmed = false;
  let tier: Tier = tierFor(map.getZoom(), null);
  let pinKeys: string[] = [];
  let labelled: string[] = [];
  let labelKey = "";

  const anim = new Animator();
  const dotsFx = new FeatureFx(map, SOURCE_IDS.placesCluster, anim, { act: 1, dim: 0, pin: 0, h: 0 });
  const timers = new Set<ReturnType<typeof setTimeout>>();
  const later = (fn: () => void, ms: number): void => {
    const t = setTimeout(() => {
      timers.delete(t);
      if (!destroyed) fn();
    }, ms);
    timers.add(t);
  };

  const has = (id: string): boolean => {
    try {
      return !!map.getLayer(id);
    } catch {
      return false;
    }
  };
  const paint = (id: string, k: string, v: unknown): void => {
    if (destroyed || !has(id)) return;
    try {
      (map as unknown as { setPaintProperty(l: string, k: string, v: unknown): void }).setPaintProperty(id, k, v);
    } catch (err) {
      console.warn("[markers]", id, k, err);
    }
  };
  const layoutProp = (id: string, k: string, v: unknown): void => {
    if (destroyed || !has(id)) return;
    (map as unknown as { setLayoutProperty(l: string, k: string, v: unknown): void }).setLayoutProperty(id, k, v);
  };

  /* ── host ────────────────────────────────────────────────────────────── */

  const host = map.getCanvasContainer();
  host.classList.add("ld-loc-markers-host");
  host.dataset.layout = layout;

  /* ── sources, image, layers ──────────────────────────────────────────── */

  const EMPTY: FeatureCollection = { type: "FeatureCollection", features: [] };
  if (!map.getSource(SOURCE_IDS.placesCluster))
    map.addSource(SOURCE_IDS.placesCluster, { type: "geojson", data: EMPTY, cluster: true, clusterMaxZoom: CLUSTER_MAX_ZOOM, clusterRadius: 48, promoteId: "id", maxzoom: 14 });
  if (!map.getSource(SOURCE_IDS.places)) map.addSource(SOURCE_IDS.places, { type: "geojson", data: EMPTY, promoteId: "id", maxzoom: 14 });
  if (!map.getSource(SOURCE_IDS.pinProxy)) map.addSource(SOURCE_IDS.pinProxy, { type: "geojson", data: EMPTY, maxzoom: 14 });
  if (!map.getSource(MARKER_IDS.routeProxy)) map.addSource(MARKER_IDS.routeProxy, { type: "geojson", data: EMPTY, maxzoom: 14, tolerance: 0.5 });
  const blank = (w: number, h: number) => ({ width: w, height: h, data: new Uint8Array(w * h * 4) });
  if (!map.hasImage(MARKER_IDS.proxyImage)) map.addImage(MARKER_IDS.proxyImage, blank(PROXY_PX, PROXY_PX));
  if (!map.hasImage(MARKER_IDS.routeProxyImage)) map.addImage(MARKER_IDS.routeProxyImage, blank(ROUTE_PROXY_PX, ROUTE_PROXY_PX));
  /** Transparent boxes sized to a DOM rect (quantised to 4 px so small reflows do not re-add them). */
  const boxSize: Record<string, string> = {};
  const q4 = (v: number): number => Math.max(4, Math.ceil(v / 4) * 4);
  function ensureBox(id: string, w: number, h: number): void {
    const W = q4(w);
    const H = q4(h);
    const key = `${W}x${H}`;
    if (boxSize[id] === key && map.hasImage(id)) return;
    try {
      if (map.hasImage(id)) map.removeImage(id);
      map.addImage(id, blank(W, H));
      boxSize[id] = key;
    } catch (err) {
      console.warn("[markers] proxy box", id, err);
    }
  }
  ensureBox(MARKER_IDS.siteLabelImage, 110, 36);
  ensureBox(MARKER_IDS.attrImage, 8, 8);

  function glLayers(m: Mood): { places: LayerSpecification[]; top: LayerSpecification[] } {
    const pl = MOODS[m].places;
    const pulse = MOODS[m].cssVars["--ld-pulse"] ?? pl.label;
    const tr = { duration: MOOD_TRANSITION_MS, delay: 0 };
    const { k, halo } = textScale(layout);
    const circle = { "circle-pitch-alignment": "map", "circle-pitch-scale": "viewport" } as const;
    const dots: LayerSpecification[] = GROUPS.map((g) => ({
      id: DOT_IDS[g],
      type: "circle",
      source: SOURCE_IDS.placesCluster,
      filter: ["all", NOT_CLUSTER, ["==", ["get", "group"], g]] as FilterSpecification,
      paint: {
        ...circle,
        "circle-color": pl.dot[g],
        "circle-color-transition": tr,
        "circle-radius": DOT_RADIUS,
        "circle-opacity": DOT_OPACITY,
        "circle-stroke-color": pl.dotStroke,
        "circle-stroke-color-transition": tr,
        "circle-stroke-width": DOT_STROKE_W,
        "circle-stroke-opacity": DOT_OPACITY,
      },
    }));
    return {
      places: [
        {
          id: LAYER_IDS.placesClusterRing2,
          type: "circle",
          source: SOURCE_IDS.placesCluster,
          filter: ["all", IS_CLUSTER, [">=", PC, 10]] as FilterSpecification,
          paint: { ...circle, "circle-radius": clusterR(8), "circle-opacity": 0, "circle-stroke-width": 1, "circle-stroke-color": pl.clusterStroke, "circle-stroke-color-transition": tr, "circle-stroke-opacity": 0.45 },
        },
        {
          id: LAYER_IDS.placesClusterRing1,
          type: "circle",
          source: SOURCE_IDS.placesCluster,
          filter: ["all", IS_CLUSTER, [">=", PC, 5]] as FilterSpecification,
          paint: { ...circle, "circle-radius": clusterR(4), "circle-opacity": 0, "circle-stroke-width": 1, "circle-stroke-color": pl.clusterStroke, "circle-stroke-color-transition": tr, "circle-stroke-opacity": 0.8 },
        },
        {
          id: LAYER_IDS.placesCluster,
          type: "circle",
          source: SOURCE_IDS.placesCluster,
          filter: IS_CLUSTER,
          paint: {
            ...circle,
            "circle-radius": clusterR(0),
            "circle-color": pl.cluster,
            "circle-color-transition": tr,
            "circle-stroke-width": 1,
            "circle-stroke-color": pl.clusterStroke,
            "circle-stroke-color-transition": tr,
          },
        },
        {
          id: MARKER_IDS.clusterHover,
          type: "circle",
          source: SOURCE_IDS.placesCluster,
          filter: IS_CLUSTER,
          paint: { ...circle, "circle-radius": clusterR(4), "circle-opacity": 0, "circle-stroke-width": 1.5, "circle-stroke-color": pulse, "circle-stroke-color-transition": tr, "circle-stroke-opacity": ["*", 0.85, HOV] as Expr },
        },
        {
          id: MARKER_IDS.clusterHover2,
          type: "circle",
          source: SOURCE_IDS.placesCluster,
          filter: ["all", IS_CLUSTER, [">=", PC, 10]] as FilterSpecification,
          paint: { ...circle, "circle-radius": clusterR(8), "circle-opacity": 0, "circle-stroke-width": 1, "circle-stroke-color": pulse, "circle-stroke-color-transition": tr, "circle-stroke-opacity": ["*", 0.6, HOV] as Expr },
        },
        {
          id: LAYER_IDS.placesClusterCount,
          type: "symbol",
          source: SOURCE_IDS.placesCluster,
          filter: IS_CLUSTER,
          layout: {
            "text-field": ["to-string", PC] as Expr,
            "text-font": [FONTSTACKS.bold],
            "text-size": ["step", PC, Math.round(12 * k * 10) / 10, 25, Math.round(13 * k * 10) / 10] as Expr,
            "text-letter-spacing": 0.02,
            "text-allow-overlap": true,
            "text-ignore-placement": true,
          },
          paint: { "text-color": pl.clusterText, "text-color-transition": tr },
        },
        ...dots,
        {
          id: MARKER_IDS.dotHover,
          type: "circle",
          source: SOURCE_IDS.placesCluster,
          filter: NOT_CLUSTER,
          paint: {
            ...circle,
            "circle-radius": DOT_RADIUS,
            "circle-opacity": 0,
            "circle-stroke-width": 2,
            "circle-stroke-color": pulse,
            "circle-stroke-color-transition": tr,
            "circle-stroke-opacity": ["*", 0.85, HOV, ["-", 1, PIN]] as Expr,
          },
        },
        {
          id: LAYER_IDS.placesLabel,
          type: "symbol",
          source: SOURCE_IDS.places,
          filter: NO_LABELS,
          layout: {
            "text-field": ["get", "name"] as Expr,
            "text-font": [FONTSTACKS.bold],
            "text-size": Math.round(11.5 * k * 10) / 10,
            "text-max-width": 9,
            "text-padding": 4,
            "text-variable-anchor": ["left", "right", "top", "bottom"],
            "text-radial-offset": 0.9,
            "text-justify": "auto",
            "symbol-sort-key": ["get", "min"] as Expr,
          },
          paint: {
            "text-color": pl.label,
            "text-color-transition": tr,
            "text-halo-color": pl.labelHalo,
            "text-halo-color-transition": tr,
            "text-halo-width": layout === "kiosk" ? 2 : 1.5 + halo,
            "text-opacity": labelsFlag ? 1 : 0,
            "text-opacity-transition": NO_TR,
          },
        },
      ],
      top: [
        {
          // the lit route keeps its line: labels step off it while a place is selected (L1-13)
          id: MARKER_IDS.routeProxy,
          type: "symbol",
          source: MARKER_IDS.routeProxy,
          layout: {
            "symbol-placement": "line",
            "symbol-spacing": ROUTE_PROXY_SPACING,
            "icon-image": MARKER_IDS.routeProxyImage,
            "icon-allow-overlap": true,
            "icon-ignore-placement": false,
            "icon-padding": 0,
          },
          paint: { "icon-opacity": 0 },
        },
        {
          id: LAYER_IDS.pinProxy,
          type: "symbol",
          source: SOURCE_IDS.pinProxy,
          layout: {
            "icon-image": ["get", "img"] as Expr,
            "icon-size": ["get", "is"] as Expr,
            "icon-anchor": ["get", "ia"] as Expr,
            "icon-offset": ["get", "ioff"] as Expr,
            "icon-allow-overlap": true,
            "icon-ignore-placement": false,
            "icon-pitch-alignment": "viewport",
            "icon-rotation-alignment": "viewport",
            "text-field": ["get", "pill"] as Expr,
            "text-font": [FONTSTACKS.bold],
            "text-size": ["get", "ts"] as Expr,
            "text-anchor": ["get", "anchor"] as Expr,
            "text-offset": ["get", "off"] as Expr,
            "text-padding": 8,
            "text-allow-overlap": true,
            "text-ignore-placement": false,
            "text-pitch-alignment": "viewport",
            "text-rotation-alignment": "viewport",
          },
          paint: { "icon-opacity": 0, "text-opacity": 0 },
        },
      ],
    };
  }

  const built = glLayers(mood);
  const before = (id: string): string | undefined => (has(id) ? id : undefined);
  for (const l of built.places) if (!has(l.id)) map.addLayer(l, before(o.beforeId));
  for (const l of built.top) if (!has(l.id)) map.addLayer(l, before(o.topId));
  const ownLayers = [...built.places, ...built.top].map((l) => l.id);
  const dotLayerIds = GROUPS.map((g) => DOT_IDS[g]);

  /* ── shader warm-up (L2-04) ──────────────────────────────────────────── */

  // Clusters draw only below z10, so their two circle programs (data-driven radius; and data-driven
  // stroke opacity for the hover ring) were linked at the first flight out to the region: a 100–900 ms
  // frame mid-flight. Draw copies of those layers once now, under the loader, on one made-up cluster at
  // the centre with opacities no pixel shows, then remove them. Same paint shape, same programs.
  let warmOff: (() => void) | null = null;
  (function warmClusterPrograms(): void {
    const src = "ld-loc-warm-clusters";
    const c = map.getCenter();
    const ids: string[] = [];
    try {
      map.addSource(src, { type: "geojson", data: { type: "FeatureCollection", features: [{ type: "Feature", properties: { point_count: 12, cluster: true }, geometry: { type: "Point", coordinates: [c.lng, c.lat] } }] } });
      for (const l of built.places) {
        if (l.id !== LAYER_IDS.placesCluster && l.id !== MARKER_IDS.clusterHover) continue;
        const paintSpec = { ...(l as { paint?: Record<string, unknown> }).paint };
        if (l.id === LAYER_IDS.placesCluster) Object.assign(paintSpec, { "circle-opacity": 0.001, "circle-stroke-opacity": 0.001 });
        const id = `${l.id}-warm`;
        map.addLayer({ ...(l as object), id, source: src, paint: paintSpec } as LayerSpecification, before(o.topId));
        ids.push(id);
      }
    } catch {
      /* nothing to warm: the first flight links them as before */
    }
    let frames = 0;
    let settled = false;
    const done = (): void => {
      if (settled) return;
      settled = true;
      map.off("render", onRender);
      clearTimeout(cap);
      warmOff = null;
      try {
        for (const id of ids) if (map.getLayer(id)) map.removeLayer(id);
        if (map.getSource(src)) map.removeSource(src);
      } catch {
        /* the map is gone */
      }
    };
    const onRender = (): void => {
      let loaded: boolean;
      try {
        loaded = !ids.length || map.isSourceLoaded(src);
      } catch {
        loaded = true; // the source is gone: nothing left to warm
      }
      if (loaded && ++frames >= 2) done();
      else map.triggerRepaint();
    };
    const cap = setTimeout(done, 4000);
    map.on("render", onRender);
    map.triggerRepaint();
    warmOff = done;
  })();

  /* ── the intro (I1): the opening line over the land alone. No dots, clusters or labels until the land has answered:
   *    hidden at the "start" beat, back at "rings" (7900, the tree rings and distance rings at the site).
   *    The short intro emits start → ui in one tick, so nothing is ever hidden there. ── */
  let introHidden = false;
  const setIntroHidden = (on: boolean): void => {
    if (destroyed || on === introHidden) return;
    introHidden = on;
    for (const l of built.places) {
      const show = !on && (l.id !== LAYER_IDS.placesLabel || labelsFlag);
      layoutProp(l.id, "visibility", show ? "visible" : "none");
    }
  };
  const onIntroBeat = ({ beat }: { beat: string }): void => {
    if (beat === "start") setIntroHidden(true);
    else if (beat === "rings" || beat === "ui" || beat === "done") setIntroHidden(false);
    // the credit arrives with the UI (640 settle, staggered): measure it once it has landed
    if (beat === "ui" || beat === "done") syncAttrSoon(beat === "ui" ? 1100 : 300);
  };
  bus.on("intro:beat", onIntroBeat);

  /* ── data ────────────────────────────────────────────────────────────── */

  function setData(list: Place[], categories: Pick<Category, "id" | "kind" | "icon">[]): void {
    places = toMarkerPlaces(list, categories);
    byId = new Map(places.map((p) => [p.id, p]));
    corridorPlace = new Map();
    for (const p of list) if (p.geometryRef) corridorPlace.set(p.geometryRef, p.id);
    const features: Feature<Point>[] = places.filter(hasDot).map((p) => ({
      type: "Feature",
      properties: { id: p.id, name: p.name, group: p.group, cat: p.category, min: p.minutes ?? 9999 },
      geometry: { type: "Point", coordinates: p.coord },
    }));
    const fc: FeatureCollection = { type: "FeatureCollection", features };
    void (map.getSource(SOURCE_IDS.placesCluster) as GeoJSONSource | undefined)?.setData(fc);
    void (map.getSource(SOURCE_IDS.places) as GeoJSONSource | undefined)?.setData(fc);
    const ids = new Set<string | number>(features.map((f) => String(f.properties?.id)));
    dotsFx.retain(ids);
    for (const p of places) {
      if (!hasDot(p)) continue;
      dotsFx.set(p.id, "act", p.group === group ? 1 : 0);
      dotsFx.set(p.id, "dim", dimmed ? 1 : 0);
    }
    if (selectedId && !byId.has(selectedId)) selectedId = null;
    labelKey = "";
    refresh();
  }

  /* ── the site mark (§D.4) ────────────────────────────────────────────── */

  const origin: LngLat = (app0.data?.origin.coord as LngLat | undefined) ?? [73.960862, 18.605185];
  const sitePoint = siteLabelPoint(app0.geo?.site, origin);
  const site = { visible: true, progress: 1, label: true };
  const siteFill = anim.value(1, (v) => {
    site.progress = v;
    applySite();
  });

  const glowEl = document.createElement("div");
  glowEl.className = "ld-loc-markers-glow";
  glowEl.setAttribute("aria-hidden", "true");
  const glowDisc = document.createElement("div");
  glowDisc.className = "ld-loc-markers-glow-disc";
  glowEl.append(glowDisc);
  const glowMarker = new maplibregl.Marker({ element: glowEl, pitchAlignment: "map", rotationAlignment: "map" }).setLngLat(sitePoint).addTo(map);

  const siteEl = document.createElement("div");
  siteEl.className = "ld-loc-markers-site";
  siteEl.setAttribute("role", "img");
  siteEl.setAttribute("aria-label", SITE_ARIA);
  const siteFx = document.createElement("div");
  siteFx.className = "ld-loc-markers-site-fx";
  siteFx.setAttribute("aria-hidden", "true");
  const siteHalo = document.createElement("div");
  siteHalo.className = "ld-loc-markers-site-halo";
  const siteMark = document.createElement("div");
  siteMark.className = "ld-loc-markers-site-mark";
  const siteName = document.createElement("div");
  siteName.className = "ld-loc-markers-site-name";
  const siteMeta = document.createElement("span");
  siteMeta.className = "ld-loc-markers-site-meta";
  siteMeta.textContent = SITE_META;
  siteName.append(document.createTextNode(SITE_LABEL), siteMeta);
  siteFx.append(siteHalo, siteMark, siteName);
  siteEl.append(siteFx);
  const siteMarker = new maplibregl.Marker({ element: siteEl, pitchAlignment: "viewport", rotationAlignment: "viewport" }).setLngLat(sitePoint).addTo(map);
  const whorl: WhorlHandle = mountWhorl(siteMark, siteMarkSize(layout));

  function applySiteSize(): void {
    const s = siteMarkSize(layout);
    whorl.setSize(s);
    siteEl.style.setProperty("--ld-loc-site-r", `${s / 2}px`);
    glowEl.style.setProperty("--ld-loc-glow-d", `${Math.round((128 * s) / 44)}px`);
  }
  function applySite(): void {
    siteEl.classList.toggle("is-hidden", !site.visible);
    glowEl.classList.toggle("is-hidden", !site.visible);
    siteEl.classList.toggle("is-unlabelled", !site.label);
    siteEl.classList.toggle("is-meta", tier >= 2);
    siteEl.classList.toggle("is-region", tier === 0);
    whorl.setProgress(site.progress);
    const pv = String(Math.round(site.progress * 1000) / 1000);
    siteEl.style.setProperty("--ld-loc-site-p", pv);
    glowEl.style.setProperty("--ld-loc-site-p", pv);
  }
  const applyGlowMood = (): void => {
    glowEl.classList.toggle("is-off", mood === "day");
    siteEl.classList.toggle("is-night", mood === "night");
  };
  applySiteSize();
  applySite();
  applyGlowMood();

  /* ── pins ────────────────────────────────────────────────────────────── */

  const views = new Map<string, PinView>();
  const free: PinView[] = [];
  const pulses = new Set<maplibregl.Marker>();
  let iconsReady = false;
  void loadIcons().then(() => {
    if (destroyed) return;
    iconsReady = true;
    const size = pinSizes(layout).icon;
    for (const v of views.values()) if (v.state !== "free") setIcon(v.dot, v.icon, v.extra !== null ? Math.round(size * 0.8) : size);
  });

  let hoverTimer: ReturnType<typeof setTimeout> | null = null;
  let pendingHover: string | null = null;
  let emittedHover: string | null = null;
  function hoverIntent(id: string | null): void {
    if (kiosk() || destroyed) return;
    if (id === pendingHover) return;
    pendingHover = id;
    if (hoverTimer) clearTimeout(hoverTimer);
    hoverTimer = null;
    if (id) {
      hoverTimer = setTimeout(() => {
        hoverTimer = null;
        if (destroyed || pendingHover !== id || emittedHover === id) return;
        emittedHover = id;
        bus.emit("map:hover-place", { id });
      }, 120);
    } else if (emittedHover) {
      emittedHover = null;
      bus.emit("map:hover-place", { id: null });
    }
  }

  function createView(): PinView {
    const el = document.createElement("div");
    el.className = "ld-loc-markers-pin";
    const fx = document.createElement("div");
    fx.className = "ld-loc-markers-fx";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "ld-pin ld-loc-markers-btn";
    const dot = document.createElement("span");
    dot.className = "ld-pin__dot ld-loc-markers-dot";
    dot.setAttribute("aria-hidden", "true");
    const pill = document.createElement("span");
    pill.className = "ld-pin__label ld-loc-markers-pill";
    pill.setAttribute("aria-hidden", "true");
    const pillName = document.createTextNode("");
    const pillMeta = document.createElement("em");
    pill.append(pillName, pillMeta);
    btn.append(dot, pill);
    fx.append(btn);
    el.append(fx);
    const marker = new maplibregl.Marker({ element: el, anchor: "center", pitchAlignment: "viewport", rotationAlignment: "viewport" });
    const v: PinView = {
      key: "",
      placeId: "",
      extra: null,
      el,
      fx,
      btn,
      dot,
      pill,
      pillName,
      pillMeta,
      marker,
      attached: false,
      state: "free",
      anim: null,
      leaveTimer: null,
      icon: "",
      pillW: 0,
      pillH: 26,
      flip: false,
      max: null,
      tucked: false,
      wantPill: false,
      labelled: false,
    };
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      if (v.state === "in" && v.placeId) bus.emit("map:click-place", { id: v.placeId });
    });
    btn.addEventListener("pointerenter", (e) => {
      if (e.pointerType !== "mouse" || v.state !== "in") return;
      el.classList.add("is-hot");
      hoverIntent(v.placeId);
    });
    btn.addEventListener("pointerleave", (e) => {
      if (e.pointerType !== "mouse") return;
      el.classList.toggle("is-hot", extHover === v.placeId);
      if (pendingHover === v.placeId) hoverIntent(null);
    });
    btn.addEventListener("focus", () => {
      if (v.state === "in" && btn.matches(":focus-visible")) hoverIntent(v.placeId);
    });
    btn.addEventListener("blur", () => {
      if (pendingHover === v.placeId) hoverIntent(null);
    });
    return v;
  }

  function assign(v: PinView, key: string): void {
    const { placeId, extra } = parseKey(key);
    const p = byId.get(placeId);
    v.key = key;
    v.placeId = placeId;
    v.extra = extra;
    const x = p && extra !== null ? p.extra[extra] : undefined;
    const coord = x ? x.coord : (p?.coord ?? sitePoint);
    const sizes = pinSizes(layout);
    v.icon = x ? "mapPin" : (p?.icon ?? "mapPin");
    if (iconsReady) setIcon(v.dot, v.icon, x ? Math.round(sizes.icon * 0.8) : sizes.icon);
    else v.dot.textContent = "";
    const pill = x ? { name: x.note, meta: "" } : pillParts(p?.name ?? placeId, p?.minutes ?? null);
    v.pillName.data = pill.name;
    v.pillMeta.textContent = pill.meta;
    v.pillMeta.style.display = pill.meta ? "" : "none";
    v.btn.setAttribute("aria-label", x ? `${p?.name ?? ""}: ${x.note}` : pinAriaLabel(p?.name ?? placeId, p?.minutes ?? null));
    v.el.classList.toggle("is-extra", !!x);
    v.el.dataset.place = placeId;
    v.marker.setLngLat(coord as LngLat);
    if (!v.attached) {
      v.marker.addTo(map);
      v.attached = true;
    }
    setClamp(v, null);
    v.pillW = 0;
  }

  /** L4-11: a pill with no room on either side wraps inside the roomier side. */
  function setClamp(v: PinView, max: number | null): void {
    if (v.max === max) return;
    v.max = max;
    v.el.classList.toggle("is-clamped", max !== null);
    if (max !== null) v.pill.style.setProperty("--ld-loc-pill-max", `${max}px`);
    else v.pill.style.removeProperty("--ld-loc-pill-max");
  }

  function runAnim(v: PinView, frames: Keyframe[], o2: KeyframeAnimationOptions): Animation | null {
    v.anim?.cancel();
    v.anim = null;
    if (typeof v.fx.animate !== "function") return null;
    try {
      v.anim = v.fx.animate(frames, o2);
      return v.anim;
    } catch {
      return null;
    }
  }

  /** Light arriving: the soft entry (focus or tier change) or the bloom arrival (scale 0.6 → 1, 420 settle). */
  function enter(v: PinView, kind: "soft" | "arrive"): void {
    if (v.leaveTimer) clearTimeout(v.leaveTimer);
    v.leaveTimer = null;
    v.state = "in";
    const from = Number(getComputedStyle(v.fx).opacity) || 0;
    if (reduced()) {
      runAnim(v, [{ opacity: Math.min(from, 0.001) }, { opacity: 1 }], { duration: kind === "arrive" ? 240 : 160, easing: "linear", fill: "backwards" });
      return;
    }
    const s0 = kind === "arrive" ? 0.6 : 0.86;
    runAnim(v, [{ opacity: from < 1 ? from : 0, transform: `scale(${s0})` }, { opacity: 1, transform: "scale(1)" }], { duration: DUR.calm, easing: EASE_CSS.settle, fill: "backwards" });
  }

  function release(v: PinView): void {
    v.anim?.cancel();
    v.anim = null;
    if (v.leaveTimer) clearTimeout(v.leaveTimer);
    v.leaveTimer = null;
    if (v.el.contains(document.activeElement)) (map.getCanvas() as HTMLElement).focus({ preventScroll: true });
    if (v.attached) v.marker.remove();
    v.attached = false;
    v.state = "free";
    v.el.className = "ld-loc-markers-pin";
    v.labelled = false;
    v.flip = false;
    v.tucked = false;
    v.btn.removeAttribute("tabindex");
    setClamp(v, null);
    v.wantPill = false;
    v.btn.classList.remove("is-hover");
    v.btn.setAttribute("aria-expanded", "false");
    v.btn.removeAttribute("aria-controls");
    if (views.get(v.key) === v) views.delete(v.key);
    if (pendingHover === v.placeId) hoverIntent(null);
    free.push(v);
  }

  /** Leave: opacity → 0 and scale → 0.9, 294 leave (§D.3). */
  function leave(v: PinView): void {
    if (v.state !== "in") return;
    v.state = "leaving";
    // If this pin has keyboard focus, focus moves to the map region (§H.1) before it goes.
    if (v.el.contains(document.activeElement)) (map.getCanvas() as HTMLElement).focus({ preventScroll: true });
    const dur = reduced() ? 160 : exitMs(DUR.calm);
    runAnim(v, [{ opacity: 1, transform: "scale(1)" }, { opacity: 0, transform: reduced() ? "scale(1)" : "scale(0.9)" }], { duration: dur, easing: reduced() ? "linear" : EASE_CSS.leave, fill: "forwards" });
    v.leaveTimer = setTimeout(() => {
      v.leaveTimer = null;
      if (v.state === "leaving") release(v);
    }, dur + 20);
  }

  function acquire(): PinView {
    const f = free.pop();
    if (f) return f;
    if (views.size >= MAX_PINS * 2) {
      // Too many still leaving: finish the oldest now.
      for (const v of views.values()) {
        if (v.state === "leaving") {
          release(v);
          return free.pop()!;
        }
      }
    }
    return createView();
  }

  function applyPins(keys: string[], arriveKey?: string): void {
    const want = new Set(keys);
    for (const v of [...views.values()]) if (!want.has(v.key) && v.state === "in") leave(v);
    const ordered: PinView[] = [];
    for (const key of keys) {
      let v = views.get(key);
      if (!v) {
        v = acquire();
        assign(v, key);
        views.set(key, v);
        enter(v, key === arriveKey ? "arrive" : "soft");
      } else if (v.state === "leaving") enter(v, key === arriveKey ? "arrive" : "soft");
      else if (key === arriveKey) enter(v, "arrive");
      ordered.push(v);
    }
    const sel = selectedId;
    for (const v of ordered) {
      const isSel = v.extra === null && v.placeId === sel;
      v.el.classList.toggle("is-sel", isSel);
      v.btn.setAttribute("aria-expanded", isSel ? "true" : "false");
      if (isSel) v.btn.setAttribute("aria-controls", "ld-card");
      else v.btn.removeAttribute("aria-controls");
      v.wantPill = tier >= 3 && !isSel;
      const hot = extHover !== null && v.placeId === extHover && v.extra === null;
      v.btn.classList.toggle("is-hover", hot);
      v.el.classList.toggle("is-hot", hot);
    }
    // Keyboard order follows the list order (selection, then nearest first, §H.1).
    let inOrder = true;
    for (let i = 1; i < ordered.length; i++) {
      if (!(ordered[i - 1].el.compareDocumentPosition(ordered[i].el) & Node.DOCUMENT_POSITION_FOLLOWING)) {
        inOrder = false;
        break;
      }
    }
    if (!inOrder) {
      const active = document.activeElement as HTMLElement | null;
      for (const v of ordered) host.appendChild(v.el);
      if (active && active !== document.activeElement && host.contains(active)) active.focus({ preventScroll: true });
    }
    pinKeys = keys;
    computeFlips();
  }

  /* Sizes read from layout are cached (L2-06b): the map size on "resize", the site's name box once per
     layout (and when the web fonts land), each pill's width when its text changes. Move handlers only
     project points and compare numbers. */
  let viewW = 0;
  let viewH = 0;
  const viewSize = (): { W: number; H: number } => {
    if (!viewW || !viewH) {
      viewW = canvas.clientWidth || host.clientWidth;
      viewH = canvas.clientHeight || host.clientHeight;
    }
    return { W: viewW, H: viewH };
  };
  let nameBox: { w: number; h: number } | null = null;
  const siteNameBox = (): { w: number; h: number } => {
    if (!nameBox || !nameBox.w) nameBox = { w: siteName.offsetWidth, h: siteName.offsetHeight };
    return nameBox;
  };
  /** Screen rects of the Whorl and (when shown) its name + meta line, at the current camera. */
  function siteRects(pad = 4): Rect[] {
    if (!site.visible) return [];
    const c = map.project(sitePoint);
    const s = siteMarkSize(layout) / 2 + pad;
    const out: Rect[] = [{ x0: c.x - s, y0: c.y - s, x1: c.x + s, y1: c.y + s }];
    if (site.label) {
      const { w, h } = siteNameBox();
      const top = tier === 0 ? c.y - s - 4 - h : c.y + s + 4;
      out.push({ x0: c.x - w / 2 - pad, y0: top - pad, x1: c.x + w / 2 + pad, y1: top + h + pad });
    }
    return out;
  }

  /** Persistent pills (z ≥ 13) and flips: nearest first, never over another pin, pill or the site mark. */
  function computeFlips(): void {
    if (destroyed) return;
    const { W, H } = viewSize();
    const sz = pinSizes(layout);
    const act: PinView[] = [];
    for (const k of pinKeys) {
      const v = views.get(k);
      if (v && v.state === "in") act.push(v);
    }
    // measure first, then write (no layout thrash); a width is measured once per text (assign resets it)
    for (const v of act) {
      if (!v.pillW) {
        v.pillW = v.pill.offsetWidth;
        v.pillH = v.pill.offsetHeight || 26;
      }
    }
    const obstacles = siteRects();
    const pts = act.map((v) => map.project(v.marker.getLngLat()));
    const res = layoutPills(
      act.map((v, i) => ({ x: pts[i].x, y: pts[i].y, w: v.pillW, h: v.pillH, want: v.wantPill })),
      { gap: sz.pillGap, hit: sz.hit, viewW: W, viewH: H, insets, obstacles },
    );
    // L1-02: in a bloom below z13, a pin on the site's name shows as its group dot (its route stays drawn)
    const z = map.getZoom();
    const blooming = focus !== null;
    let changed = false;
    let tuckChanged = false;
    act.forEach((v, i) => {
      const { show, flip, max } = res[i];
      if (flip !== v.flip) {
        v.flip = flip;
        v.el.classList.toggle("is-flip", flip);
        changed = true;
      }
      if (show !== v.labelled) {
        v.labelled = show;
        v.el.classList.toggle("is-labelled", show);
        changed = true;
      }
      setClamp(v, show ? null : (max ?? null));
      const tuck = tucksUnderSite(pts[i], sz.disc, obstacles, {
        zoom: z,
        bloom: blooming,
        selected: v.extra === null && v.placeId === selectedId,
        hot: v.extra === null && v.placeId === extHover,
        extra: v.extra !== null,
      });
      if (tuck !== v.tucked) {
        v.tucked = tuck;
        v.el.classList.toggle("is-tucked", tuck);
        v.btn.tabIndex = tuck ? -1 : 0;
        if (tuck && v.el.contains(document.activeElement)) (map.getCanvas() as HTMLElement).focus({ preventScroll: true });
        tuckChanged = true;
      }
    });
    if (tuckChanged) applyDotPins();
    if (changed || tuckChanged) applyProxies();
  }

  /* ── dots, labels, proxies ───────────────────────────────────────────── */

  function tuckedPlaces(): Set<string> {
    const s = new Set<string>();
    for (const v of views.values()) if (v.tucked && v.state === "in" && v.extra === null) s.add(v.placeId);
    return s;
  }

  function pinnedPlaces(): Set<string> {
    const s = new Set<string>();
    for (const k of pinKeys) {
      const { placeId, extra } = parseKey(k);
      if (extra === null) s.add(placeId);
    }
    return s;
  }

  function applyDotPins(): void {
    const pinned = pinnedPlaces();
    // a tucked pin (under the site's name) shows its dot instead, at full strength (it is in the bloom)
    const tucked = tuckedPlaces();
    for (const id of tucked) pinned.delete(id);
    const r0 = reduced();
    for (const p of places) {
      if (!hasDot(p)) continue;
      const on = pinned.has(p.id) ? 1 : 0;
      void dotsFx.to(p.id, "pin", on, r0 ? 160 : on ? DUR.quick : exitMs(DUR.calm), on ? EASE.settle : EASE.leave);
      const dim = dimmed && !tucked.has(p.id) ? 1 : 0;
      if (dotsFx.target(p.id, "dim") !== dim) void dotsFx.to(p.id, "dim", dim, r0 ? 160 : DUR.quick, EASE.settle);
    }
  }

  function applyLabels(): void {
    const ids = labelIds({ tier, group, category, selectedId, pinned: pinnedPlaces(), places });
    const key = ids.join("|");
    if (key === labelKey) return;
    labelKey = key;
    labelled = ids;
    if (!has(LAYER_IDS.placesLabel)) return;
    map.setFilter(LAYER_IDS.placesLabel, ids.length ? (["in", ["get", "id"], ["literal", ids]] as FilterSpecification) : NO_LABELS);
  }

  function applyProxies(): void {
    const src = map.getSource(SOURCE_IDS.pinProxy) as GeoJSONSource | undefined;
    if (!src) return;
    const sz = pinSizes(layout);
    const features: Feature<Point>[] = [];
    const icon = { img: MARKER_IDS.proxyImage, ia: "center", ioff: [0, 0] };
    for (const key of pinKeys) {
      const v = views.get(key);
      if (!v || v.state !== "in" || v.tucked) continue;
      const p = byId.get(v.placeId);
      const x = p && v.extra !== null ? p.extra[v.extra] : undefined;
      const pp = p ? pillParts(p.name, p.minutes) : null;
      const pill = v.labelled ? (x ? x.note : `${pp?.name ?? ""}  ${pp?.meta ?? ""}`.trim()) : "";
      const em = (sz.pillGap + 10) / sz.pillText;
      features.push({
        type: "Feature",
        properties: { id: key, ...icon, is: sz.hit / PROXY_PX, ts: sz.pillText, pill, anchor: v.flip ? "right" : "left", off: [v.flip ? -em : em, 0] },
        geometry: { type: "Point", coordinates: (x ? x.coord : p?.coord ?? sitePoint) as number[] },
      });
    }
    if (site.visible) {
      const s = siteMarkSize(layout);
      features.push({
        type: "Feature",
        properties: { id: "site", ...icon, is: s / PROXY_PX, ts: 12, pill: "", anchor: "center", off: [0, 0] },
        geometry: { type: "Point", coordinates: sitePoint },
      });
      if (site.label) {
        // L1-02: the whole label box (name and the LOHEGAON · PUNE line), measured from the DOM label
        // (kiosk sizes included), so place, point, corridor and future labels keep off it.
        const { w, h } = siteNameBox();
        ensureBox(MARKER_IDS.siteLabelImage, w + 8, h + 6);
        const up = tier === 0;
        features.push({
          type: "Feature",
          properties: { id: "site-label", img: MARKER_IDS.siteLabelImage, ia: up ? "bottom" : "top", ioff: [0, (up ? -1 : 1) * (s / 2 + 5)], is: 1, ts: 12, pill: "", anchor: "center", off: [0, 0] },
          geometry: { type: "Point", coordinates: sitePoint },
        });
      }
    }
    if (attr) {
      features.push({
        type: "Feature",
        properties: { id: "attr", img: MARKER_IDS.attrImage, ia: "center", ioff: [0, 0], is: 1, ts: 12, pill: "", anchor: "center", off: [0, 0] },
        geometry: { type: "Point", coordinates: attr.at },
      });
    }
    void src.setData({ type: "FeatureCollection", features });
  }

  /* ── the attribution's collision twin (L1-09) ────────────────────────── */

  /** Where the credit sits: the lngLat under its centre when the camera last rested. */
  let attr: { at: LngLat; x: number; y: number } | null = null;
  let attrEl: Element | null = null;
  let attrRo: ResizeObserver | null = null;
  /** Re-anchors the credit's twin under the credit (at rest only: never inside a ride or a flight). */
  function syncAttr(force = false): void {
    if (destroyed) return;
    const el = document.querySelector(ATTR_SELECTOR);
    if (el !== attrEl) {
      attrRo?.disconnect();
      attrRo = null;
      attrEl = el;
      if (el && typeof ResizeObserver !== "undefined") {
        attrRo = new ResizeObserver(() => syncAttr(true));
        attrRo.observe(el);
      }
    }
    let next: typeof attr = null;
    if (el) {
      const r = el.getBoundingClientRect();
      const m = host.getBoundingClientRect();
      const x = r.left + r.width / 2 - m.left;
      const y = r.top + r.height / 2 - m.top;
      const { W, H } = viewSize();
      if (r.width > 0 && r.height > 0 && x >= 0 && y >= 0 && x <= W && y <= H) {
        ensureBox(MARKER_IDS.attrImage, r.width + 8, r.height + 4);
        const ll = map.unproject([x, y]);
        next = { at: [ll.lng, ll.lat], x, y };
      }
    }
    let same = !attr && !next;
    if (attr && next && Math.abs(attr.x - next.x) < 0.5 && Math.abs(attr.y - next.y) < 0.5) {
      const p = map.project(attr.at);
      same = Math.hypot(p.x - next.x, p.y - next.y) < 1;
    }
    if (!force && same) return;
    attr = next;
    applyProxies();
  }
  let attrTimer: ReturnType<typeof setTimeout> | null = null;
  const syncAttrSoon = (ms = 0): void => {
    if (attrTimer) clearTimeout(attrTimer);
    attrTimer = setTimeout(() => {
      attrTimer = null;
      if (!destroyed && !map.isMoving() && getApp().phase !== "drive") syncAttr();
    }, ms);
  };

  /* ── the selected route's collision twin (L1-13) ─────────────────────── */

  let routeProxyId: string | null = null;
  function applyRouteProxy(): void {
    const src = map.getSource(MARKER_IDS.routeProxy) as GeoJSONSource | undefined;
    if (!src) return;
    const app = getApp();
    const place = selectedId ? app.data?.places.find((p) => p.id === selectedId) : undefined;
    const rt = place && app.layers.routes ? routeFor(place, app.routes) : null;
    const id = rt ? rt.id : null;
    if (id === routeProxyId) return;
    routeProxyId = id;
    const coords = rt ? thin(rt.coords as LngLat[], Math.max(1, Math.ceil(rt.coords.length / 1500))) : [];
    void src.setData(coords.length > 1 ? { type: "FeatureCollection", features: [{ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: coords } }] } : EMPTY);
  }

  function applyActive(animate: boolean): void {
    const r0 = reduced();
    for (const p of places) {
      if (!hasDot(p)) continue;
      const v = p.group === group ? 1 : 0;
      if (!animate) dotsFx.set(p.id, "act", v);
      else void dotsFx.to(p.id, "act", v, r0 ? 160 : DUR.calm, r0 ? EASE.linear : EASE.drift);
    }
  }

  function refresh(arriveKey?: string): void {
    if (destroyed) return;
    const keys = computePins({ tier, selectedId, focus, category, places: byId });
    if (arriveKey && !keys.includes(arriveKey)) arriveKey = undefined;
    applyPins(keys, arriveKey);
    applyDotPins();
    applyLabels();
    applyProxies();
    applyRouteProxy();
    applySite();
  }

  /* ── hover visuals on dots and clusters (feature-state h) ────────────── */

  const hoverOn = new Set<string | number>();
  function applyDotHover(): void {
    const want = new Set<string | number>();
    if (extHover && byId.has(extHover) && hasDot(byId.get(extHover)!)) want.add(extHover);
    if (ptrHover) want.add(ptrHover.id);
    const r0 = reduced();
    for (const id of hoverOn) if (!want.has(id)) void dotsFx.to(id, "h", 0, r0 ? 160 : 168, EASE.leave);
    for (const id of want) if (!hoverOn.has(id)) void dotsFx.to(id, "h", 1, r0 ? 160 : DUR.quick, EASE.settle);
    hoverOn.clear();
    for (const id of want) hoverOn.add(id);
  }

  /* ── hit testing (§D.1: a box of 8 / 22 / 28 px, nearest wins) ───────── */

  type Hit = { kind: "dot"; id: string } | { kind: "cluster"; id: number; coord: LngLat } | { kind: "corridor"; id: string };

  function query(box: [PointLike, PointLike], layers: string[]): MapGeoJSONFeature[] {
    const ls = layers.filter(has);
    if (!ls.length) return [];
    try {
      return map.queryRenderedFeatures(box, { layers: ls });
    } catch {
      return [];
    }
  }

  function hitTest(px: number, py: number, tol: number): Hit | null {
    const box: [PointLike, PointLike] = [
      [px - tol, py - tol],
      [px + tol, py + tol],
    ];
    const pinned = pinnedPlaces();
    const cands: Candidate<Hit>[] = [];
    const layers = [LAYER_IDS.placesCluster, ...dotLayerIds];
    for (const f of query(box, layers)) {
      if (f.geometry.type !== "Point") continue;
      const c = f.geometry.coordinates as LngLat;
      const pt = map.project(c);
      const props = f.properties as Record<string, unknown>;
      if (props.cluster) {
        cands.push({ item: { kind: "cluster", id: Number(props.cluster_id), coord: c }, x: pt.x, y: pt.y, rank: 1 });
      } else {
        const id = String(props.id);
        if (pinned.has(id)) continue;
        cands.push({ item: { kind: "dot", id }, x: pt.x, y: pt.y, rank: 0 });
      }
    }
    const best = nearest(cands, px, py);
    if (best) return best;
    // The corridor is the road and future places' mark (§D.1): it answers too.
    const corridorLayers: string[] = [];
    if (getApp().layers.future) corridorLayers.push(LAYER_IDS.futureLine);
    if (group === "A" && map.getZoom() >= 10) corridorLayers.push(LAYER_IDS.roadsLine);
    corridorLayers.push("ld-roads-lit");
    const t2 = Math.max(4, Math.min(tol, 10));
    for (const f of query(
      [
        [px - t2, py - t2],
        [px + t2, py + t2],
      ],
      corridorLayers,
    )) {
      const props = f.properties as Record<string, unknown>;
      if (f.layer.id === "ld-roads-lit" && !(Number((f.state as Record<string, unknown> | undefined)?.a ?? 0) > 0.5)) continue;
      const pid = (typeof props.placeId === "string" && props.placeId) || corridorPlace.get(String(props.id));
      if (pid && byId.has(pid)) return { kind: "corridor", id: pid };
    }
    return null;
  }

  const onClick = (e: MapMouseEvent): void => {
    if (destroyed) return;
    const t = e.originalEvent?.target as Element | null;
    if (t && typeof t.closest === "function" && t.closest(".ld-loc-markers-pin")) return;
    const hit = hitTest(e.point.x, e.point.y, hitTolerance(layout));
    if (!hit) {
      bus.emit("map:click-empty");
      return;
    }
    if (hit.kind === "cluster") {
      // §D.2: easeTo the expansion zoom + 0.3, 900 drift. With clusterMaxZoom 9, a cluster drawn at z ≥ 9 always
      // splits at z10, so the move starts on the click; the worker is asked only below z9 (it can be busy
      // parsing tiles for a second or more right after a jump).
      const go = (z: number): void => {
        if (destroyed) return;
        const r0 = reduced();
        map.easeTo({ center: hit.coord, zoom: Math.min(map.getMaxZoom(), z + 0.3), duration: r0 ? 240 : 900, easing: r0 ? EASE.linear : EASE.drift, essential: true });
      };
      if (Math.floor(map.getZoom()) >= CLUSTER_MAX_ZOOM) go(CLUSTER_MAX_ZOOM + 1);
      else
        void (map.getSource(SOURCE_IDS.placesCluster) as GeoJSONSource | undefined)
          ?.getClusterExpansionZoom(hit.id)
          .then(go)
          .catch(() => {});
      return;
    }
    bus.emit("map:click-place", { id: hit.id });
  };

  // Pointer moves are coalesced to one hit test per frame on the shared ticker (the one clock).
  let hoverQueued: (() => void) | null = null;
  let lastPt: { x: number; y: number } | null = null;
  const canvas = map.getCanvas();
  const processHover = (): boolean => {
    hoverQueued = null;
    if (destroyed || !lastPt) return false;
    const hit = hitTest(lastPt.x, lastPt.y, hitTolerance(layout));
    const next = hit && (hit.kind === "dot" || hit.kind === "cluster") ? { kind: hit.kind, id: hit.id } : null;
    if ((next?.id ?? null) !== (ptrHover?.id ?? null)) {
      ptrHover = next;
      applyDotHover();
    }
    canvas.style.cursor = hit ? "pointer" : "";
    hoverIntent(hit && hit.kind !== "cluster" ? hit.id : null);
    return false;
  };
  const onMouseMove = (e: MapMouseEvent): void => {
    if (kiosk() || destroyed) return;
    const oe = e.originalEvent as PointerEvent | MouseEvent | undefined;
    if (oe && "pointerType" in oe && oe.pointerType && oe.pointerType !== "mouse") return;
    const t = oe?.target as Element | null | undefined;
    if (t && typeof t.closest === "function" && t.closest(".ld-loc-markers-pin")) {
      // Over a glass pin: the pin has its own hover (and hover intent); the land beneath rests.
      lastPt = null;
      if (ptrHover) {
        ptrHover = null;
        applyDotHover();
      }
      canvas.style.cursor = "";
      return;
    }
    lastPt = { x: e.point.x, y: e.point.y };
    if (!hoverQueued) hoverQueued = ticker.add(processHover);
  };
  const onMouseOut = (): void => {
    lastPt = null;
    if (ptrHover) {
      ptrHover = null;
      applyDotHover();
    }
    canvas.style.cursor = "";
    hoverIntent(null);
  };
  map.on("click", onClick);
  map.on("mousemove", onMouseMove);
  map.on("mouseout", onMouseOut);

  /* ── camera ──────────────────────────────────────────────────────────── */

  const checkTier = (): void => {
    if (destroyed) return;
    const t = tierFor(map.getZoom(), tier);
    if (t !== tier) {
      tier = t;
      refresh();
    }
  };
  const onZoom = (): void => checkTier(); // O(1) unless the tier changes
  /** The drive writes the camera every frame (jumpTo: movestart + moveend each time). */
  const riding = (): boolean => getApp().phase === "drive";
  const onMoveStart = (): void => {
    if (!("moving" in host.dataset)) host.dataset.moving = "";
  };
  const onMoveEnd = (): void => {
    checkTier();
    // During the ride the glass stays unblurred and nothing but the selected pin (no pill) is on the
    // map, so flips and the credit's twin wait until the camera is the visitor's again (L2-06b).
    if (riding()) return;
    delete host.dataset.moving;
    computeFlips();
    syncAttrSoon(60);
  };
  const onResize = (): void => {
    viewW = viewH = 0;
    computeFlips();
    syncAttrSoon(120);
  };
  map.on("zoom", onZoom);
  map.on("movestart", onMoveStart);
  map.on("moveend", onMoveEnd);
  map.on("resize", onResize);
  // the web fonts change the site name's box
  void document.fonts?.ready.then(() => {
    if (destroyed) return;
    nameBox = null;
    applyProxies();
    computeFlips();
  });

  /* ── store ───────────────────────────────────────────────────────────── */

  let refreshQueued = false;
  const queueRefresh = (): void => {
    if (refreshQueued) return;
    refreshQueued = true;
    queueMicrotask(() => {
      refreshQueued = false;
      refresh();
    });
  };
  const unsub = appStore.subscribe((s, prev) => {
    if (destroyed) return;
    if (s.group !== prev.group) {
      group = s.group;
      applyActive(true);
      queueRefresh();
    }
    if (s.category !== prev.category) {
      category = s.category;
      queueRefresh();
    }
    if (s.insets !== prev.insets) {
      insets = { ...s.insets };
      computeFlips();
      syncAttrSoon(700); // the chrome settles (sheet snap 640) before the credit is measured
    }
    if (s.phase !== prev.phase) {
      if (prev.phase === "drive" && !map.isMoving()) {
        delete host.dataset.moving;
        computeFlips();
      }
      syncAttrSoon(700);
    }
    if (s.layers.routes !== prev.layers.routes) applyRouteProxy();
    if (s.layers.labels !== prev.layers.labels) {
      labelsFlag = s.layers.labels;
      const r0 = reduced();
      if (labelsFlag && !introHidden) layoutProp(LAYER_IDS.placesLabel, "visibility", "visible");
      paint(LAYER_IDS.placesLabel, "text-opacity-transition", { duration: r0 ? 160 : labelsFlag ? DUR.calm : exitMs(DUR.calm), delay: 0 });
      paint(LAYER_IDS.placesLabel, "text-opacity", labelsFlag ? 1 : 0);
      if (!labelsFlag) later(() => !labelsFlag && layoutProp(LAYER_IDS.placesLabel, "visibility", "none"), (r0 ? 160 : exitMs(DUR.calm)) + 40);
    }
    if (s.layout !== prev.layout) {
      layout = s.layout;
      applyLayout();
    }
    if (s.data !== prev.data && s.data) setData(s.data.places, s.data.categories);
  });

  function applyLayout(): void {
    host.dataset.layout = layout;
    const { k, halo } = textScale(layout);
    layoutProp(LAYER_IDS.placesLabel, "text-size", Math.round(11.5 * k * 10) / 10);
    paint(LAYER_IDS.placesLabel, "text-halo-width", layout === "kiosk" ? 2 : 1.5 + halo);
    layoutProp(LAYER_IDS.placesClusterCount, "text-size", ["step", PC, Math.round(12 * k * 10) / 10, 25, Math.round(13 * k * 10) / 10]);
    const size = pinSizes(layout).icon;
    for (const v of views.values()) {
      if (v.state !== "free" && iconsReady) setIcon(v.dot, v.icon, v.extra !== null ? Math.round(size * 0.8) : size);
      setClamp(v, null);
      v.pillW = 0;
    }
    applySiteSize();
    viewW = viewH = 0;
    nameBox = null;
    computeFlips();
    applyProxies();
    syncAttrSoon(700);
  }

  /* ── initial data ────────────────────────────────────────────────────── */

  if (app0.data) setData(app0.data.places, app0.data.categories);
  else refresh();
  syncAttrSoon(300);

  /* ── API ─────────────────────────────────────────────────────────────── */

  const api: MarkersHandle = {
    setPlaces(list: Place[], categories: Category[]): void {
      if (destroyed) return;
      setData(list, categories);
    },

    setFocusSet(ids: string[] | null): void {
      if (destroyed) return;
      focus = ids ? [...ids] : null;
      refresh();
    },

    setSelected(id: string | null): void {
      if (destroyed) return;
      const p = id ? byId.get(id) : undefined;
      const next = p && !p.isSite ? p.id : null;
      if (next === selectedId) return;
      selectedId = next;
      refresh();
    },

    setHovered(id: string | null): void {
      if (destroyed) return;
      const next = id && byId.has(id) ? id : null;
      if (next === extHover) return;
      extHover = next;
      for (const v of views.values()) {
        if (v.state !== "in") continue;
        const hot = next !== null && v.placeId === next && v.extra === null;
        v.btn.classList.toggle("is-hover", hot);
        v.el.classList.toggle("is-hot", hot);
      }
      applyDotHover();
      if (focus !== null) computeFlips(); // a pin tucked under the site's name comes out while it is asked about
    },

    setDimmed(on: boolean): void {
      if (destroyed || on === dimmed) return;
      dimmed = on;
      const r0 = reduced();
      const tucked = tuckedPlaces();
      for (const p of places) {
        if (!hasDot(p)) continue;
        const d = on && !tucked.has(p.id);
        void dotsFx.to(p.id, "dim", d ? 1 : 0, r0 ? 160 : d ? DUR.calm : exitMs(DUR.calm), r0 ? EASE.linear : d ? EASE.drift : EASE.leave);
      }
    },

    arrive(id: string, ao?: { ring?: boolean }): void {
      if (destroyed) return;
      const p = byId.get(id);
      if (!p || p.isSite) return;
      if (!pinKeys.includes(id)) {
        if (focus === null) focus = [];
        if (!focus.includes(id)) focus.push(id);
      }
      refresh(id);
      if (ao?.ring !== false) api.pulse(p.coord, { count: 1, radiusPx: Math.round((36 * pulseRadius(layout)) / 56), durationMs: 1200 });
    },

    pulse(at: LngLat, po?: { count?: number; staggerMs?: number; radiusPx?: number; durationMs?: number }): void {
      if (destroyed || reduced()) return; // reduced motion: no rings (§E.10)
      const count = Math.max(1, Math.min(6, Math.round(po?.count ?? 3)));
      const stagger = Math.max(0, po?.staggerMs ?? 160);
      const R = Math.max(4, po?.radiusPx ?? pulseRadius(layout));
      const dur = Math.max(60, po?.durationMs ?? 1600);
      const NS = "http://www.w3.org/2000/svg";
      const el = document.createElement("div");
      el.className = "ld-loc-markers-pulse";
      el.setAttribute("aria-hidden", "true");
      const svg = document.createElementNS(NS, "svg");
      const size = 2 * (R + 4);
      svg.setAttribute("class", "ld-pulse");
      svg.setAttribute("width", String(size));
      svg.setAttribute("height", String(size));
      svg.setAttribute("viewBox", `0 0 ${size} ${size}`);
      for (let i = 0; i < count; i++) {
        for (const cls of ["ld-loc-markers-ring-shade", "ld-loc-markers-ring"]) {
          const c = document.createElementNS(NS, "circle");
          c.setAttribute("cx", String(R + 4));
          c.setAttribute("cy", String(R + 4));
          c.setAttribute("r", String(R));
          c.setAttribute("class", cls);
          c.style.animationDuration = `${dur}ms`;
          c.style.animationDelay = `${i * stagger}ms`;
          svg.append(c);
        }
      }
      el.append(svg);
      const mk = new maplibregl.Marker({ element: el, pitchAlignment: "map", rotationAlignment: "map" }).setLngLat(at).addTo(map);
      // Beneath the pins and the site mark: the rings are on the ground.
      host.insertBefore(el, glowEl.nextSibling);
      pulses.add(mk);
      later(() => {
        pulses.delete(mk);
        mk.remove();
      }, dur + stagger * (count - 1) + 80);
    },

    setSite(so): void {
      if (destroyed) return;
      const wasVisible = site.visible;
      const wasLabel = site.label;
      if (so.visible !== undefined) site.visible = so.visible;
      if (so.progress !== undefined) {
        siteFill.cancel();
        siteFill.v = site.progress = Math.max(0, Math.min(1, so.progress));
      }
      if (so.label !== undefined) site.label = so.label;
      applySite();
      if (wasVisible !== site.visible || wasLabel !== site.label) applyProxies();
    },

    setMood(m: Mood, mo?: { duration?: number }): void {
      if (destroyed || !MOODS[m]) return;
      mood = m;
      const dur = reduced() ? 160 : Math.max(0, mo?.duration ?? MOOD_TRANSITION_MS);
      const next = glLayers(m);
      for (const l of next.places as Array<LayerSpecification & { paint?: Record<string, unknown> }>) {
        for (const [key, value] of Object.entries(l.paint ?? {})) {
          if (!/-color$/.test(key)) continue;
          paint(l.id, `${key}-transition`, { duration: dur, delay: 0 });
          paint(l.id, key, value);
        }
      }
      applyGlowMood();
    },

    tier: () => tier,

    fillSite(fo): Promise<boolean> {
      if (destroyed) return Promise.resolve(false);
      const r0 = reduced();
      site.visible = true;
      siteFill.set(fo?.from ?? 0);
      applyProxies();
      return siteFill.to(1, r0 ? 160 : Math.max(0, fo?.durationMs ?? DUR.scene), r0 ? EASE.linear : EASE.drift);
    },

    debug(): MarkersDebug {
      return {
        tier,
        pins: [...pinKeys],
        labelled: [...labelled],
        selected: selectedId,
        focus: focus ? [...focus] : null,
        hovered: extHover ?? (ptrHover ? String(ptrHover.id) : null),
        dimmed,
        site: { ...site },
        poolSize: views.size + free.length,
      };
    },

    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      warmOff?.();
      unsub();
      bus.off("intro:beat", onIntroBeat);
      map.off("click", onClick);
      map.off("mousemove", onMouseMove);
      map.off("mouseout", onMouseOut);
      map.off("zoom", onZoom);
      map.off("movestart", onMoveStart);
      map.off("moveend", onMoveEnd);
      map.off("resize", onResize);
      attrRo?.disconnect();
      attrRo = null;
      if (attrTimer) clearTimeout(attrTimer);
      hoverQueued?.();
      if (hoverTimer) clearTimeout(hoverTimer);
      for (const t of timers) clearTimeout(t);
      timers.clear();
      for (const v of [...views.values(), ...free]) {
        v.anim?.cancel();
        if (v.leaveTimer) clearTimeout(v.leaveTimer);
        v.marker.remove();
      }
      views.clear();
      free.length = 0;
      for (const mk of pulses) mk.remove();
      pulses.clear();
      siteMarker.remove();
      glowMarker.remove();
      whorl.destroy();
      dotsFx.destroy();
      anim.destroy();
      canvas.style.cursor = "";
      host.classList.remove("ld-loc-markers-host");
      delete host.dataset.layout;
      delete host.dataset.moving;
      try {
        for (const id of [...ownLayers].reverse()) if (map.getLayer(id)) map.removeLayer(id);
        for (const id of [SOURCE_IDS.placesCluster, SOURCE_IDS.places, SOURCE_IDS.pinProxy, MARKER_IDS.routeProxy]) if (map.getSource(id)) map.removeSource(id);
        for (const id of [MARKER_IDS.proxyImage, MARKER_IDS.siteLabelImage, MARKER_IDS.attrImage, MARKER_IDS.routeProxyImage]) if (map.hasImage(id)) map.removeImage(id);
      } catch {
        /* the map is already gone */
      }
    },
  };
  return api;
}

// The contract's factory signature (the controller imports createMarkers through it).
const _factory: CreateMarkers = createMarkers;
void _factory;
